import React, { useState, useEffect } from "react";
import { LucideIcon } from "lucide-react";
import { cn } from "../../lib/utils";

// Enhanced CSS Variables for the new layout system
export const enhancedPageLayoutStyles = {
  // Layout container styles
  layout: {
    // Main container that takes full height and manages scrolling
    container: "h-full flex flex-col bg-background",
    // Fixed header section (title, controls, actions)
    header:
      "flex-shrink-0 bg-card border-b border-border px-4 sm:px-6 lg:px-8 py-6",
    // Scrollable content area
    content: "flex-1 overflow-y-auto",
    // Content padding
    contentPadding: "",
  },
  // Header section styles
  header: {
    container: "flex items-center justify-between",
    left: "flex items-center gap-3",
    right: "flex items-center gap-4",
    title: "text-3xl font-bold text-content-text",
    subtitle: "text-content-text-muted",
    icon: "w-8 h-8 text-primary",
  },
  // Filter section styles (fixed below header)
  filters: {
    container:
      "flex-shrink-0 bg-card border-b border-border px-4 sm:px-6 lg:px-8 py-4",
    content: "flex flex-col lg:flex-row gap-4",
    search: "flex-1 relative",
    searchInput:
      "w-full pl-10 pr-4 py-2 bg-input-bg border border-input-border text-input-text placeholder-input-placeholder rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent",
    searchIcon:
      "absolute left-3 top-1/2 transform -translate-y-1/2 text-input-placeholder w-4 h-4",
    filterButtons: "flex gap-2",
    filterButton:
      "flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors",
    filterButtonActive: "bg-primary/10 border-primary text-primary",
    filterButtonInactive:
      "bg-card border-border text-content-text-muted hover:bg-hover",
    select:
      "px-3 py-2 bg-input-bg border border-input-border text-input-text rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent custom-select",
  },
  // View switcher styles
  viewSwitcher: {
    container: "flex bg-sidebar-hover rounded-lg p-1",
    button: "p-2 rounded",
    buttonActive: "bg-card shadow",
    buttonInactive: "hover:bg-hover",
  },
  // Table styles
  table: {
    container: "bg-card border border-border rounded-lg hidden lg:block", // Hide on smaller screens
    wrapper: "", // Remove overflow-x-auto to allow full width
    table: "w-full min-w-full", // Ensure table takes full width
    thead: "bg-sidebar-hover border-b border-border",
    th: "px-0 py-3 text-left text-xs font-medium text-content-text uppercase tracking-wider",
    tbody: "bg-card divide-y divide-border",
    tr: "", // Remove hover background for table rows
    td: "px-0 py-0", // Remove all padding for table cells
    actionButton:
      "p-1 text-content-text-muted hover:text-primary transition-colors",
    actionButtonDanger:
      "p-1 text-content-text-muted hover:text-destructive transition-colors",
  },
  // Card styles
  card: {
    container:
      "bg-card border border-border rounded-lg overflow-hidden hover:shadow-lg transition-shadow",
    content: "p-6",
    header: "flex items-start justify-between mb-4",
    title: "text-lg font-semibold text-content-text mb-2",
    description: "text-content-text-muted text-sm mb-4 line-clamp-2",
    footer: "flex items-center justify-between",
    meta: "flex items-center gap-2 text-sm text-content-text-muted",
    actions: "flex items-center gap-2",
  },
  // List styles
  list: {
    container: "space-y-4 block lg:hidden", // Show on smaller screens, hide on large screens
    item: "bg-card border border-border rounded-lg p-6 hover:shadow-md transition-shadow",
    content: "flex items-center justify-between",
    left: "flex items-center gap-4",
    right: "flex items-center gap-4",
  },
  // Loading and error states
  states: {
    loading: "text-center py-12",
    loadingSpinner:
      "animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4",
    loadingText: "text-content-text-muted",
    error: "text-center py-12",
    errorIcon: "w-12 h-12 text-destructive mx-auto mb-4",
    errorTitle: "text-lg font-medium text-content-text mb-2",
    errorText: "text-content-text-muted",
    empty: "text-center py-12",
    emptyIcon: "w-12 h-12 text-content-text-muted mx-auto mb-4",
    emptyTitle: "text-lg font-medium text-content-text mb-2",
    emptyText: "text-content-text-muted mb-4",
  },
  // Button styles
  button: {
    primary:
      "flex items-center gap-2 bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary/90 transition-colors",
    secondary:
      "flex items-center gap-2 bg-card border border-border text-content-text-muted px-4 py-2 rounded-lg light:hover:bg-hover transition-colors",
    icon: "p-2 text-content-text-muted hover:text-primary transition-colors",
    iconDanger:
      "p-2 text-content-text-muted hover:text-destructive transition-colors",
    // Action buttons for sidebar (consistent with EnhancedActionButton - not full-width)
    actionPrimary:
      "px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors flex items-center justify-center gap-2 font-medium",
    actionPrimaryDisabled:
      "px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2 font-medium",
    actionDanger:
      "px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center justify-center gap-2 font-medium",
    actionDangerDisabled:
      "px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2 font-medium",
    actionSecondary:
      "px-4 py-2 text-foreground bg-card border border-input rounded-lg hover:bg-muted transition-colors font-medium",
  },
} as const;

// Enhanced Page Header Component (Fixed)
interface EnhancedPageHeaderProps {
  icon: LucideIcon;
  title: string;
  subtitle: string;
  actionButton?: React.ReactNode;
  viewSwitcher?: React.ReactNode;
}

export const EnhancedPageHeader: React.FC<EnhancedPageHeaderProps> = ({
  icon: Icon,
  title,
  subtitle,
  actionButton,
  viewSwitcher,
}) => {
  return (
    <div className={enhancedPageLayoutStyles.layout.header}>
      <div className={enhancedPageLayoutStyles.header.container}>
        <div className={enhancedPageLayoutStyles.header.left}>
          <Icon className={enhancedPageLayoutStyles.header.icon} />
          <div>
            <h1 className={enhancedPageLayoutStyles.header.title}>{title}</h1>
            <p className={enhancedPageLayoutStyles.header.subtitle}>
              {subtitle}
            </p>
          </div>
        </div>
        <div className={enhancedPageLayoutStyles.header.right}>
          {viewSwitcher}
          {actionButton}
        </div>
      </div>
    </div>
  );
};

// Enhanced Filter Section Component (Fixed)
interface EnhancedFilterSectionProps {
  searchTerm: string;
  onSearchChange: (value: string) => void;
  searchPlaceholder: string;
  children?: React.ReactNode;
}

export const EnhancedFilterSection: React.FC<EnhancedFilterSectionProps> = ({
  searchTerm,
  onSearchChange,
  searchPlaceholder,
  children,
}) => {
  return (
    <div className={enhancedPageLayoutStyles.filters.container}>
      <div className={enhancedPageLayoutStyles.filters.content}>
        <div className={enhancedPageLayoutStyles.filters.search}>
          <Search className={enhancedPageLayoutStyles.filters.searchIcon} />
          <input
            type="text"
            placeholder={searchPlaceholder}
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            className={enhancedPageLayoutStyles.filters.searchInput}
          />
        </div>
        {children}
      </div>
    </div>
  );
};

// Enhanced View Switcher Component
interface EnhancedViewSwitcherProps {
  viewMode: "cards" | "list" | "table";
  onViewChange: (view: "cards" | "list" | "table") => void;
}

export const EnhancedViewSwitcher: React.FC<EnhancedViewSwitcherProps> = ({
  viewMode,
  onViewChange,
}) => {
  return (
    <div className={enhancedPageLayoutStyles.viewSwitcher.container}>
      <button
        onClick={() => onViewChange("cards")}
        className={`${enhancedPageLayoutStyles.viewSwitcher.button} ${
          viewMode === "cards"
            ? enhancedPageLayoutStyles.viewSwitcher.buttonActive
            : enhancedPageLayoutStyles.viewSwitcher.buttonInactive
        }`}
        title="Cards View"
      >
        <Grid3X3 className="w-4 h-4" />
      </button>
      <button
        onClick={() => onViewChange("list")}
        className={`${enhancedPageLayoutStyles.viewSwitcher.button} ${
          viewMode === "list"
            ? enhancedPageLayoutStyles.viewSwitcher.buttonActive
            : enhancedPageLayoutStyles.viewSwitcher.buttonInactive
        }`}
        title="List View"
      >
        <List className="w-4 h-4" />
      </button>
      <button
        onClick={() => onViewChange("table")}
        className={`${enhancedPageLayoutStyles.viewSwitcher.button} ${
          viewMode === "table"
            ? enhancedPageLayoutStyles.viewSwitcher.buttonActive
            : enhancedPageLayoutStyles.viewSwitcher.buttonInactive
        }`}
        title="Table View"
      >
        <Table className="w-4 h-4" />
      </button>
    </div>
  );
};

// Responsive View Switcher Component
interface ResponsiveViewSwitcherProps {
  viewMode: "cards" | "list" | "table";
  onViewChange: (view: "cards" | "list" | "table") => void;
}

export const ResponsiveViewSwitcher: React.FC<ResponsiveViewSwitcherProps> = ({
  viewMode,
  onViewChange,
}) => {
  return (
    <div className={enhancedPageLayoutStyles.viewSwitcher.container}>
      <button
        onClick={() => onViewChange("cards")}
        className={`${enhancedPageLayoutStyles.viewSwitcher.button} ${
          viewMode === "cards"
            ? enhancedPageLayoutStyles.viewSwitcher.buttonActive
            : enhancedPageLayoutStyles.viewSwitcher.buttonInactive
        }`}
        title="Cards View"
      >
        <Grid3X3 className="w-4 h-4" />
      </button>
      <button
        onClick={() => onViewChange("list")}
        className={`${enhancedPageLayoutStyles.viewSwitcher.button} ${
          viewMode === "list"
            ? enhancedPageLayoutStyles.viewSwitcher.buttonActive
            : enhancedPageLayoutStyles.viewSwitcher.buttonInactive
        }`}
        title="List View"
      >
        <List className="w-4 h-4" />
      </button>
      {/* Hide table button on smaller screens */}
      <button
        onClick={() => onViewChange("table")}
        className={`${enhancedPageLayoutStyles.viewSwitcher.button} ${
          viewMode === "table"
            ? enhancedPageLayoutStyles.viewSwitcher.buttonActive
            : enhancedPageLayoutStyles.viewSwitcher.buttonInactive
        } hidden lg:flex`}
        title="Table View"
      >
        <Table className="w-4 h-4" />
      </button>
    </div>
  );
};

// Enhanced Action Button Component
interface EnhancedActionButtonProps {
  onClick: () => void;
  icon: LucideIcon;
  label: React.ReactNode;
  variant?: "primary" | "secondary" | "danger";
  className?: string;
  disabled?: boolean;
  hideLabelOnMobile?: boolean;
  title?: string;
}

export const EnhancedActionButton: React.FC<EnhancedActionButtonProps> = ({
  onClick,
  icon: Icon,
  label,
  variant = "primary",
  className = "",
  disabled = false,
  hideLabelOnMobile = false,
  title,
}) => {
  const baseClass =
    variant === "danger"
      ? "flex items-center justify-center gap-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
      : variant === "primary"
        ? enhancedPageLayoutStyles.button.primary
        : enhancedPageLayoutStyles.button.secondary;

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={cn(
        baseClass,
        "h-[38px] justify-center disabled:opacity-70 disabled:cursor-not-allowed",
        (!label || hideLabelOnMobile) ? "px-3 sm:px-4" : "px-4",
        className
      )}
    >
      <Icon className="w-4 h-4 shrink-0" />
      {label && (
        hideLabelOnMobile ? (
          <span className="hidden sm:inline font-medium text-sm">{label}</span>
        ) : (
          typeof label === "string" ? (
            <span className="font-medium text-sm">{label}</span>
          ) : (
            label
          )
        )
      )}
    </button>
  );
};

// Enhanced Icon Button Component
interface EnhancedIconButtonProps {
  onClick: (e?: React.MouseEvent<HTMLButtonElement>) => void;
  icon: LucideIcon;
  title: string;
  variant?: "default" | "danger";
  className?: string;
}

export const EnhancedIconButton: React.FC<EnhancedIconButtonProps> = ({
  onClick,
  icon: Icon,
  title,
  variant = "default",
  className = "",
}) => {
  const baseClass =
    variant === "danger"
      ? enhancedPageLayoutStyles.button.iconDanger
      : enhancedPageLayoutStyles.button.icon;

  return (
    <button
      onClick={onClick}
      className={`${baseClass} ${className}`}
      title={title}
    >
      <Icon className="w-4 h-4" />
    </button>
  );
};

// Main Enhanced Page Layout Component
interface EnhancedPageLayoutProps {
  children: React.ReactNode;
  header?: React.ReactNode;
  filters?: React.ReactNode;
  className?: string;
}

export const EnhancedPageLayout: React.FC<EnhancedPageLayoutProps> = ({
  children,
  header,
  filters,
  className = "",
}) => {
  return (
    <div
      className={`${enhancedPageLayoutStyles.layout.container} ${className}`}
    >
      {/* Sticky container for both Header and Filters to prevent overlap and ensure visibility */}
      {(header || filters) && (
        <div className="sticky top-0 z-20 bg-card shadow-sm transition-all duration-300">
          {header && header}
          {filters && filters}
        </div>
      )}

      {/* Content Area - Using flex-1 to fill remaining space */}
      <div className={cn(
        enhancedPageLayoutStyles.layout.content,
        "overflow-visible h-auto flex-none" // Let parent scroll container handle scrolling
      )}>
        <div className={enhancedPageLayoutStyles.layout.contentPadding}>
          {children}
        </div>
      </div>
    </div>
  );
};

// Hook for responsive view mode and sidebar management
export const useResponsiveLayout = (
  initialViewMode: "cards" | "list" | "table" = "table",
) => {
  // Restore preferred desktop view from localStorage if available
  const savedPreferred =
    typeof window !== "undefined"
      ? (localStorage.getItem("preferredViewMode") as
          | "cards"
          | "list"
          | "table"
          | null)
      : null;
  const [preferredDesktopView, setPreferredDesktopView] = useState<
    "cards" | "list" | "table"
  >(savedPreferred || initialViewMode);
  const [viewMode, setViewMode] = useState<"cards" | "list" | "table">(
    savedPreferred || initialViewMode,
  );
  const [isFilterExpanded, setIsFilterExpanded] = useState(() => {
    if (typeof window !== "undefined") {
      return window.innerWidth >= 1024;
    }
    return false;
  });
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkScreenSize = () => {
      const mobile = window.innerWidth < 1024; // lg breakpoint
      setIsMobile(mobile);

      if (mobile) {
        // On mobile, always show cards for readability
        if (viewMode !== "cards") setViewMode("cards");
      } else {
        // On desktop, restore the user's preferred view
        if (viewMode !== preferredDesktopView) {
          setViewMode(preferredDesktopView);
        }
      }
    };

    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);

    return () => window.removeEventListener("resize", checkScreenSize);
  }, [viewMode]);

  const handleViewChange = (newView: "cards" | "list" | "table") => {
    // On mobile, lock to cards view
    if (isMobile && newView !== "cards") {
      return;
    }

    setViewMode(newView);

    // If not mobile, remember the user's preference
    if (!isMobile) {
      setPreferredDesktopView(newView);
      try {
        localStorage.setItem("preferredViewMode", newView);
      } catch {}
    }
  };

  return {
    viewMode,
    setViewMode: handleViewChange,
    isMobile,
    isFilterExpanded,
    setIsFilterExpanded,
    shouldCollapseSidebar: isMobile,
    hideViewSwitcher: isMobile,
  };
};

// Import required icons
import { Search, Grid3X3, List, Table } from "lucide-react";
